import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { LOCATIONS, BRAND, LOCALES, LOCATION_IDS, parseEmbedParams } from "./config";
import { makeT, type I18nKey } from "./i18n";
import type { CreateResponse, LocationId, PatientForm, Slot } from "./types";
import { captureLead, createBooking, fetchSlots, invalidateSlots, prefetchSlots, primeSlots, submitWaitlist } from "./api";
import { announceBrand, conversion, initAnalytics, observeHeight, scrollToTop, track } from "./analytics";
import { getRecaptchaToken, preloadRecaptcha } from "./recaptcha";
import { BackButton, CheckMark, Field, Header, PrimaryButton, Progress } from "./ui";

// Lead-first flow: contact info is captured + persisted BEFORE slots are shown,
// so an abandon at slot-selection still leaves the front desk a followable lead.
type Step = "location" | "lead" | "time" | "finish" | "success" | "waitlist_done" | "handoff";

const PROGRESS: Partial<Record<Step, number>> = { location: 0, lead: 1, time: 2, finish: 3 };

// Weeks of availability to ask for. 9 x 7 = 63 days covers the 60-day look-ahead
// Mason asked for on every brand (2026-08-25); at the old 8 the window stopped
// mid-October and Braces520 lost its Oct 20-22 openings. The function floors this
// at the same policy, so a stale bundle can't shorten the window.
const SLOT_WEEKS = 9;

const emptyForm: PatientForm = {
  firstName: "", lastName: "", dob: "", phone: "", email: "",
  isMinor: true, guardianFirstName: "", guardianLastName: "", relationship: "Parent",
  chiefComplaint: "", insuranceCompany: "", insuranceMemberId: "", company: "",
};

const digits = (s: string) => s.replace(/\D/g, "");
const validPhone = (s: string) => { const d = digits(s); return d.length === 10 || (d.length === 11 && d[0] === "1"); };
// Required since 2026-07: email-less bookings landed in Ortho2 with a synthetic
// placeholder that front desks flagged as a LeadSigma mismatch (BOPISD-8008).
// Keep the regex in sync with emailOk in clinical-workflow bondLeadCapture.ts.
const validEmail = (s: string) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(s.trim());
function validDOB(s: string): boolean {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(s.trim());
  if (!m) return false;
  const mo = +m[1], da = +m[2], yr = +m[3];
  if (mo < 1 || mo > 12 || da < 1 || da > 31 || yr < 1900 || yr > new Date().getFullYear()) return false;
  const d = new Date(yr, mo - 1, da);
  return d.getMonth() === mo - 1 && d.getDate() === da;
}
function fmtPhone(s: string): string {
  const d = digits(s).slice(0, 10);
  if (d.length < 4) return d;
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}
function fmtDOB(s: string): string {
  const d = digits(s).slice(0, 8);
  return [d.slice(0, 2), d.slice(2, 4), d.slice(4, 8)].filter(Boolean).join("/");
}

/** RFC 5545 text escaping: backslash, comma, semicolon, newline. */
const icsEsc = (s: string) => s.replace(/\\/g, "\\\\").replace(/([,;])/g, "\\$1").replace(/\n/g, "\\n");

/**
 * The number to give a patient for the office they picked. Brands that publish
 * a distinct line per office (Las Vegas East/West) override the brand phone, so
 * the confirmation and the calendar invite don't send an East patient to West.
 */
function officePhone(location: LocationId | null): { phone: string; href: string } {
  // Null on the waitlist/handoff screens when no office was picked: the brand
  // line is the right answer there, not a guess at one of the offices.
  const loc = location ? LOCATIONS[location] : undefined;
  if (!loc?.phone) return { phone: BRAND.phone, href: BRAND.phoneHref };
  return { phone: loc.phone, href: loc.phoneHref ?? `tel:+1${digits(loc.phone)}` };
}

// Calendar-invite wording. Only brands that give the consult away say "free";
// Beglin charges for adult consultations, and the invite is the copy the
// patient still has in front of them on the day of the visit. Written as whole
// phrases rather than a "Free " prefix so both variants read naturally and the
// free-consult brands keep exactly the text they ship today.
const CAL_TITLE = BRAND.consultIsFree ? "Free Consultation" : "Consultation";
const CAL_DESC = BRAND.consultIsFree ? "Free orthodontic consultation" : "Orthodontic consultation";
const CAL_REMINDER = BRAND.consultIsFree ? "Free consultation tomorrow" : "Consultation tomorrow";

function buildICS(startIso: string, location: LocationId): string {
  const start = new Date(startIso);
  const end = new Date(start.getTime() + 30 * 60000);
  const z = (d: Date) => d.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
  const loc = LOCATIONS[location];
  return [
    "BEGIN:VCALENDAR", "VERSION:2.0", `PRODID:-//${BRAND.name}//Booking//EN`, "BEGIN:VEVENT",
    `UID:${Date.now()}@${BRAND.slug}.bond-booking`, `DTSTAMP:${z(new Date())}`, `DTSTART:${z(start)}`, `DTEND:${z(end)}`,
    `SUMMARY:${icsEsc(`${BRAND.name} - ${CAL_TITLE}`)}`,
    `LOCATION:${icsEsc(`${loc.name}, ${loc.addressLines.join(", ")}`)}`,
    `DESCRIPTION:${icsEsc(`${CAL_DESC} with ${BRAND.name}. Questions? Call ${officePhone(location).phone}.${loc.mapsUrl ? ` Directions: ${loc.mapsUrl}` : ""}`)}`,
    // Day-before reminder; calendar apps surface it as a notification.
    "BEGIN:VALARM", "TRIGGER:-PT24H", "ACTION:DISPLAY",
    `DESCRIPTION:${icsEsc(`${CAL_REMINDER} - ${BRAND.name}`)}`, "END:VALARM",
    "END:VEVENT", "END:VCALENDAR",
  ].join("\r\n");
}

/** Prefilled Google Calendar template link (date, time, office, phone). */
function gcalUrl(startIso: string, location: LocationId): string {
  const start = new Date(startIso);
  const end = new Date(start.getTime() + 30 * 60000);
  const z = (d: Date) => d.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
  const loc = LOCATIONS[location];
  const p = new URLSearchParams({
    action: "TEMPLATE",
    text: `${BRAND.name} - ${CAL_TITLE}`,
    dates: `${z(start)}/${z(end)}`,
    location: `${loc.name}, ${loc.addressLines.join(", ")}`,
    details: `${CAL_DESC} with ${BRAND.name}. Questions? Call ${officePhone(location).phone}.${loc.mapsUrl ? `\nDirections: ${loc.mapsUrl}` : ""}`,
  });
  return `https://calendar.google.com/calendar/render?${p.toString()}`;
}
function downloadICS(ics: string) {
  const url = URL.createObjectURL(new Blob([ics], { type: "text/calendar" }));
  const a = document.createElement("a");
  a.href = url; a.download = `${BRAND.slug}.ics`; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** "Tuesday, August 11 · 11:20 AM" in the office timezone, locale-aware. */
function fmtSlotLong(iso: string, tz: string, locale: string): string {
  const tag = locale === "es" ? "es-US" : "en-US";
  const d = new Date(iso);
  const day = new Intl.DateTimeFormat(tag, { weekday: "long", month: "long", day: "numeric", timeZone: tz }).format(d);
  const time = new Intl.DateTimeFormat(tag, { hour: "numeric", minute: "2-digit", timeZone: tz }).format(d);
  return `${day} · ${time}`;
}

/**
 * Resolve the Ortho2 Edge Booking URL for a location: a per-office override
 * (multi-catalog brands like Las Vegas) else the brand-level URL. Undefined
 * means the brand books natively (no embedded scheduler).
 */
function ortho2UrlFor(location: LocationId | null): string | undefined {
  return (location && LOCATIONS[location]?.ortho2BookingUrl) || BRAND.ortho2BookingUrl;
}

export function App() {
  const params = useMemo(parseEmbedParams, []);
  const [locale, setLocale] = useState(params.locale);
  const t = useMemo(() => makeT(locale), [locale]);

  // Keep the document language honest for search engines and screen readers
  // (es mode used to stay lang="en", mispronouncing every Spanish string).
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const [step, setStep] = useState<Step>(params.location ? "lead" : "location");
  const [location, setLocation] = useState<LocationId | null>(params.location);
  const [leadDone, setLeadDone] = useState(false);
  const [leadId, setLeadId] = useState<string | null>(null);
  /** Where Continue on the lead step should land: "finish" when the visitor
   *  came back via the Edit affordance (slot already picked), else "time". */
  const [postLeadStep, setPostLeadStep] = useState<"time" | "finish">("time");
  const [slots, setSlots] = useState<Slot[] | null>(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  /** A slot FETCH failed (network/backend) — distinct from a genuinely empty search. */
  const [slotsFailed, setSlotsFailed] = useState(false);
  const [slotsAttempt, setSlotsAttempt] = useState(0);
  const [showIns, setShowIns] = useState(false);
  const [slot, setSlot] = useState<Slot | null>(null);
  // The opening we were confirming got taken; the time step explains why the
  // patient is back and that the times now shown are current.
  const [slotTaken, setSlotTaken] = useState(false);
  const [form, setForm] = useState<PatientForm>(emptyForm);
  const [errors, setErrors] = useState<Partial<Record<keyof PatientForm, string>>>({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<CreateResponse | null>(null);
  const [submitErr, setSubmitErr] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    initAnalytics(params.parentOrigin);
    preloadRecaptcha();
    track("widget_loaded", { locale, presetLocation: params.location ?? null });
    announceBrand({ name: BRAND.name, phone: BRAND.phone, phoneHref: BRAND.phoneHref, consultIsFree: BRAND.consultIsFree });
  }, []);

  useEffect(() => {
    if (rootRef.current) return observeHeight(rootRef.current);
  }, []);

  // After tapping a bottom-of-step CTA the next step would render with the
  // page still scrolled mid-widget, hiding its title; reset on every step
  // change (but not on mount — yanking the parent page on load is rude).
  const stepMounted = useRef(false);
  useEffect(() => {
    if (!stepMounted.current) { stepMounted.current = true; return; }
    scrollToTop();
  }, [step]);

  // Warm availability the moment an office is known (preset widgets: on load) —
  // the backend mints its captcha token and searches slots while the visitor
  // fills the lead form, so the time step usually renders instantly.
  useEffect(() => {
    if (location) prefetchSlots(location, SLOT_WEEKS);
  }, [location]);

  // Surface the prefetched soonest opening ON the lead step, so asking for
  // contact info reads as a value exchange rather than a toll gate. Reads the
  // same cached promise the prefetch started; no extra backend load.
  const [soonestTeaser, setSoonestTeaser] = useState<Slot | null>(null);
  useEffect(() => {
    if (step !== "lead" || !location) return;
    let live = true;
    setSoonestTeaser(null);
    fetchSlots(location, SLOT_WEEKS).then((r) => {
      if (live && r.success !== false && r.slots[0]) setSoonestTeaser(r.slots[0]);
    });
    return () => { live = false; };
  }, [step, location]);

  // Load slots when we land on the time step for a chosen office. A failed
  // fetch renders as an error-with-retry, never as the "no openings" waitlist
  // fork — a transient error must not divert bookable visitors.
  useEffect(() => {
    if (step !== "time" || !location) return;
    let live = true;
    setLoadingSlots(true);
    setSlots(null);
    setSlotsFailed(false);
    fetchSlots(location, SLOT_WEEKS).then((r) => {
      if (!live) return;
      setLoadingSlots(false);
      if (r.success === false) {
        setSlotsFailed(true);
        return;
      }
      setSlots(r.slots);
      track("slots_viewed", {
        locationId: location,
        count: r.slots.length,
        days: new Set(r.slots.map((s) => s.iso.slice(0, 10))).size,
      });
    });
    return () => { live = false; };
  }, [step, location, slotsAttempt]);

  // Per-field validity, mirrored from the submit-time checks, so a corrected
  // field sheds its error immediately instead of staying red until the next
  // submit click. Full validation still runs on submit.
  const FIELD_OK: Partial<Record<keyof PatientForm, (v: string) => boolean>> = {
    firstName: (v) => !!v.trim(),
    lastName: (v) => !!v.trim(),
    phone: validPhone,
    email: validEmail,
    dob: validDOB,
    guardianFirstName: (v) => !!v.trim(),
    guardianLastName: (v) => !!v.trim(),
  };
  const set = (k: keyof PatientForm, v: string | boolean) => {
    setForm((f) => {
      const next = { ...f, [k]: v };
      // The DOB directly above the under-18 toggle already answers it: keep the
      // toggle in sync with the typed birthdate (still manually overridable),
      // so adults stop tripping guardian "Required" errors from the Yes default.
      if (k === "dob" && typeof v === "string" && validDOB(v)) {
        const [mo, da, yr] = v.split("/").map(Number);
        const age = (Date.now() - new Date(yr, mo - 1, da).getTime()) / (365.25 * 86_400_000);
        next.isMinor = age < 18;
      }
      return next;
    });
    if (typeof v === "string" && FIELD_OK[k]?.(v)) {
      setErrors((e) => {
        if (!e[k]) return e;
        const next = { ...e };
        delete next[k];
        return next;
      });
    }
  };

  // Background partial capture: a visitor who types a reachable phone and a name
  // but never clicks Continue is still a followable lead. Fires once typing has been quiet
  // for 6s (Continue within that window cancels it via leadDone); the backend
  // upserts by leadId, so later submits update the same record.
  const leadIdRef = useRef<string | null>(null);
  leadIdRef.current = leadId;
  useEffect(() => {
    if (step !== "lead" || !location || leadDone) return;
    if (!validPhone(form.phone) || !form.firstName.trim() || form.company.trim()) return;
    const timer = setTimeout(() => {
      track("lead_partial", { locationId: location });
      void getRecaptchaToken("lead").then((token) =>
        captureLead({
          location, partial: true, leadId: leadIdRef.current ?? undefined,
          patient: { firstName: form.firstName, lastName: form.lastName, phone: form.phone, email: form.email },
          analytics: params.analytics, recaptchaToken: token, locale,
        }).then((r) => { if (r.leadId) setLeadId(r.leadId); }),
      );
    }, 6000);
    return () => clearTimeout(timer);
  }, [step, location, leadDone, form.firstName, form.lastName, form.phone, form.email]);

  function toggleLang() {
    const next = locale === "en" ? "es" : "en";
    setLocale(next);
    track("language_changed", { locale: next });
  }

  function chooseLocation(id: LocationId) {
    setLocation(id);
    track("location_selected", { locationId: id });
    setStep(leadDone ? "time" : "lead");
  }

  /** On failed submit, put the visitor on the first problem instead of leaving
   *  the error above the fold while the CTA appears to do nothing. */
  function focusFirstError(e: Partial<Record<keyof PatientForm, string>>, order: (keyof PatientForm)[]) {
    const first = order.find((k) => e[k]);
    if (!first) return;
    const el = document.getElementById(`bbg-f-${first}`) as HTMLElement | null;
    el?.focus({ preventScroll: true });
    el?.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  function submitLead() {
    if (!location) return;
    const e: Partial<Record<keyof PatientForm, string>> = {};
    if (!form.firstName.trim()) e.firstName = t("c_invalid_name");
    if (!form.lastName.trim()) e.lastName = t("c_invalid_name");
    if (!validPhone(form.phone)) e.phone = t("c_invalid_phone");
    if (!validEmail(form.email)) e.email = t("c_invalid_email");
    setErrors(e);
    if (Object.keys(e).length) {
      focusFirstError(e, ["firstName", "lastName", "phone", "email"]);
      return;
    }
    // Honeypot tripped -> pretend success, persist nothing.
    if (form.company.trim()) { setResult({ success: true, requestId: "skipped" }); setStep("success"); return; }

    const isUpdate = leadDone;
    setLeadDone(true);
    track(isUpdate ? "lead_updated" : "lead_captured", { locationId: location });
    // Outage handoff: brands with an Ortho2 booking URL (set while our own Edge
    // Booking access is down) capture the lead here, then send the visitor to
    // Ortho2's booking site to pick a time. Others continue to our slot picker.
    const handoffUrl = ortho2UrlFor(location);
    if (handoffUrl) {
      track("ortho2_handoff", { locationId: location });
      setStep("handoff");
    } else {
      setStep(postLeadStep); // snappy: move on; persist the lead in the background.
      setPostLeadStep("time");
    }
    void getRecaptchaToken("lead").then((token) =>
      captureLead({
        location: location!,
        // On edits, reuse the lead id so the backend updates the same record
        // (corrected phone/name reaches the front desk once, not as a dupe).
        leadId: leadId ?? undefined,
        patient: { firstName: form.firstName, lastName: form.lastName, phone: form.phone, email: form.email },
        analytics: params.analytics,
        recaptchaToken: token,
        locale,
      }).then((r) => { if (r.leadId) setLeadId(r.leadId); }),
    );
    // The embedded scheduler renders inline on the handoff step; no new tab.
  }

  function chooseSlot(s: Slot) {
    setSlot(s);
    setSlotTaken(false);
    track("slot_selected", { iso: s.iso });
    setStep("finish");
    track("details_started", {});
  }

  function validateFinish(): boolean {
    const e: Partial<Record<keyof PatientForm, string>> = {};
    if (!validDOB(form.dob)) e.dob = t("c_invalid_dob");
    if (form.isMinor) {
      if (!form.guardianFirstName.trim()) e.guardianFirstName = t("c_required");
      if (!form.guardianLastName.trim()) e.guardianLastName = t("c_required");
    }
    setErrors(e);
    if (Object.keys(e).length) {
      focusFirstError(e, ["dob", "guardianFirstName", "guardianLastName"]);
      return false;
    }
    return true;
  }

  async function submit() {
    if (!slot || !location) return;
    if (!validateFinish()) return;
    setSubmitErr(null);
    setSubmitting(true);
    track("booking_submitted", { iso: slot.iso });
    const token = await getRecaptchaToken("book");
    const res = await createBooking({
      location, searchKey: slot.searchKey, leadId: leadId ?? undefined,
      patient: form, analytics: params.analytics, recaptchaToken: token, locale,
    });
    setSubmitting(false);
    if (res.success) {
      setResult(res);
      setStep("success");
      track("booking_succeeded", { requestId: res.requestId, iso: slot.iso });
      conversion({
        value: params.conversionValue, currency: "USD", requestId: res.requestId,
        locationId: location, appointmentTime: res.appointmentTime ?? slot.iso, gclid: params.analytics.gclid,
      });
    } else if (res.reason === "slot_taken") {
      // Someone took that opening between selection and confirm. The backend
      // already looked up current availability, so re-offer it straight away
      // instead of bouncing the patient through another spinner.
      const fresh = res.alternatives ?? [];
      if (fresh.length > 0) primeSlots(location, fresh, LOCATIONS[location].timezone);
      else invalidateSlots(location);
      setSlot(null);
      setSlotTaken(true);
      setStep("time");
      track("slot_taken", { iso: slot.iso, alternatives: fresh.length });
    } else {
      // A real failure. Show the friendly line (backend errors can be raw) and
      // drop the cached load so a retry re-reads availability.
      invalidateSlots(location);
      setSubmitErr(t("book_failed"));
      track("booking_failed", { error: res.error });
    }
  }

  async function requestCallback() {
    if (!location) return;
    setSubmitting(true);
    const token = await getRecaptchaToken("waitlist");
    await submitWaitlist({ location, leadId: leadId ?? undefined, patient: form, analytics: params.analytics, recaptchaToken: token, locale });
    setSubmitting(false);
    track("waitlist_submitted", { locationId: location });
    setStep("waitlist_done");
  }

  const progress = PROGRESS[step];
  const embedUrl = ortho2UrlFor(location);

  return (
    <div class={`bbg${params.popup ? " bbg--popup" : ""}`} ref={rootRef}>
      <Header t={t} onLang={toggleLang} showLang={LOCALES.length > 1} />
      {progress !== undefined ? <Progress total={4} current={progress} /> : null}

      {step === "location" && (
        <div>
          <h2 class="bbg-title">{t("loc_title")}</h2>
          <p class="bbg-sub">{t("loc_subtitle")}</p>
          <div class="bbg-cards">
            {(Object.keys(LOCATIONS) as LocationId[]).map((id) => (
              <button key={id} class="bbg-card" onClick={() => chooseLocation(id)}>
                <h3>{LOCATIONS[id].name}</h3>
                {LOCATIONS[id].addressLines.map((l) => <p key={l}>{l}</p>)}
              </button>
            ))}
          </div>
        </div>
      )}

      {step === "lead" && (
        <div>
          <h2 class="bbg-title">{t("lead_title")}</h2>
          <p class="bbg-sub">{t("lead_sub")}</p>
          {location && soonestTeaser ? (
            <div class="bbg-nextopen">
              <span class="label">{t("lead_next_opening")}</span>
              <span class="when">{fmtSlotLong(soonestTeaser.iso, LOCATIONS[location].timezone, locale)}</span>
            </div>
          ) : null}
          <div class="bbg-row">
            {/* Patient names get autocomplete=off: the form-filler is usually the
                parent, whose browser profile would mis-fill the child's name. */}
            <Field id="bbg-f-firstName" label={t("details_first")} error={errors.firstName}>
              <input class={`bbg-input${errors.firstName ? " err" : ""}`} name="patient-first-name" autocomplete="off" value={form.firstName} onInput={(e) => set("firstName", (e.target as HTMLInputElement).value)} />
            </Field>
            <Field id="bbg-f-lastName" label={t("details_last")} error={errors.lastName}>
              <input class={`bbg-input${errors.lastName ? " err" : ""}`} name="patient-last-name" autocomplete="off" value={form.lastName} onInput={(e) => set("lastName", (e.target as HTMLInputElement).value)} />
            </Field>
          </div>
          <Field id="bbg-f-phone" label={t("details_phone")} error={errors.phone} help={t("details_phone_help")}>
            <input class={`bbg-input${errors.phone ? " err" : ""}`} type="tel" name="phone" autocomplete="tel" inputMode="tel" placeholder="(555) 555-5555" value={form.phone} onInput={(e) => set("phone", fmtPhone((e.target as HTMLInputElement).value))} />
          </Field>
          <Field id="bbg-f-email" label={t("details_email")} error={errors.email}>
            <input class={`bbg-input${errors.email ? " err" : ""}`} type="email" name="email" inputMode="email" autocomplete="email" value={form.email} onInput={(e) => set("email", (e.target as HTMLInputElement).value)} />
          </Field>

          {/* honeypot — off-screen; bots fill it, humans never see it. The label/name
              must NOT resemble a real contact field: Chrome profile-autofill classifies
              fields by label text and ignores autocomplete="off" on address-like fields,
              so a "Company" label gets silently filled for real visitors using autofill
              (which fake-successes them past the booking flow). */}
          <div class="bbg-hp" aria-hidden="true">
            <label>Leave this field empty<input name="bbg_xf" tabIndex={-1} autocomplete="off" value={form.company} onInput={(e) => set("company", (e.target as HTMLInputElement).value)} /></label>
          </div>

          <div class="bbg-actions">
            {!params.location ? <BackButton label={t("c_back")} onClick={() => setStep("location")} /> : null}
            <PrimaryButton onClick={submitLead}>{postLeadStep === "finish" ? t("c_next") : t("lead_cta")}</PrimaryButton>
          </div>
          <p class="bbg-recaptcha">{t("lead_sms_consent")}</p>
        </div>
      )}

      {step === "time" && location && (
        <TimeStep
          t={t} locale={locale} tz={LOCATIONS[location].timezone}
          officeName={LOCATIONS[location].name}
          slots={slots} loading={loadingSlots} onPick={chooseSlot}
          failed={slotsFailed}
          slotTaken={slotTaken}
          onRetry={() => { invalidateSlots(location); setSlotsAttempt((a) => a + 1); }}
          onBack={() => setStep("lead")}
          onChangeOffice={() => { setSlots(null); setStep("location"); }}
          onWaitlist={requestCallback} submitting={submitting}
          multiOffice={LOCATION_IDS.length > 1}
        />
      )}

      {step === "finish" && slot && location && (
        <div>
          <h2 class="bbg-title">{t("finish_title")}</h2>
          <p class="bbg-sub">{t("finish_sub")}</p>

          <div class="bbg-review" style="margin-bottom:16px">
            <div class="line"><span class="k">{t("review_when")}</span><span class="v">{slot.datetime}</span></div>
            <div class="line">
              <span class="k">{t("review_where")}</span>
              <span class="v">
                <a class="bbg-maplink" href={LOCATIONS[location].mapsUrl} target="_blank" rel="noopener">{LOCATIONS[location].name}</a>
              </span>
            </div>
            <div class="line">
              <span class="k">{t("review_contact")}</span>
              <span class="v">
                {form.firstName} {form.lastName} · {form.phone}{" "}
                <button class="bbg-lang" onClick={() => { setPostLeadStep("finish"); setStep("lead"); }}>
                  {t("review_edit")}
                </button>
                {/* The email carries the confirmation — surface it so a typo is
                    catchable behind the same Edit affordance. */}
                <span class="bbg-review-email">{form.email}</span>
              </span>
            </div>
          </div>

          <Field id="bbg-f-dob" label={t("details_dob")} error={errors.dob}>
            <input class={`bbg-input${errors.dob ? " err" : ""}`} name="patient-dob" autocomplete="off" inputMode="numeric" placeholder={t("details_dob_ph")} value={form.dob} onInput={(e) => set("dob", fmtDOB((e.target as HTMLInputElement).value))} />
          </Field>

          <Field label={t("details_minor_q")}>
            <div class="bbg-toggle">
              <button class={form.isMinor ? "on" : ""} onClick={() => set("isMinor", true)}>{t("details_yes")}</button>
              <button class={!form.isMinor ? "on" : ""} onClick={() => set("isMinor", false)}>{t("details_no")}</button>
            </div>
          </Field>

          {form.isMinor && (
            <>
              <p class="bbg-sub" style="margin-top:4px">{t("details_guardian_title")}</p>
              <div class="bbg-row">
                {/* Guardian fields ARE the form-filler's own name — let autofill help. */}
                <Field id="bbg-f-guardianFirstName" label={t("details_guardian_first")} error={errors.guardianFirstName}>
                  <input class={`bbg-input${errors.guardianFirstName ? " err" : ""}`} name="given-name" autocomplete="given-name" value={form.guardianFirstName} onInput={(e) => set("guardianFirstName", (e.target as HTMLInputElement).value)} />
                </Field>
                <Field id="bbg-f-guardianLastName" label={t("details_guardian_last")} error={errors.guardianLastName}>
                  <input class={`bbg-input${errors.guardianLastName ? " err" : ""}`} name="family-name" autocomplete="family-name" value={form.guardianLastName} onInput={(e) => set("guardianLastName", (e.target as HTMLInputElement).value)} />
                </Field>
              </div>
              <Field id="bbg-f-relationship" label={t("details_relationship")}>
                <select class="bbg-select" value={form.relationship} onChange={(e) => set("relationship", (e.target as HTMLSelectElement).value)}>
                  <option value="Parent">{t("rel_parent")}</option>
                  <option value="Guardian">{t("rel_guardian")}</option>
                  <option value="Grandparent">{t("rel_grandparent")}</option>
                  <option value="Other">{t("rel_other")}</option>
                </select>
              </Field>
            </>
          )}

          <Field id="bbg-f-chiefComplaint" label={t("details_reason")}>
            <select class="bbg-select" value={form.chiefComplaint} onChange={(e) => set("chiefComplaint", (e.target as HTMLSelectElement).value)}>
              <option value="">—</option>
              <option value="Braces consultation">{t("reason_braces")}</option>
              <option value="Invisalign / clear aligners">{t("reason_invisalign")}</option>
              <option value="Child's first orthodontic visit">{t("reason_child")}</option>
              <option value="Other">{t("reason_other")}</option>
            </select>
          </Field>

          {!showIns ? (
            <button class="bbg-btn bbg-btn-ghost" onClick={() => { setShowIns(true); track("insurance_step", {}); }}>+ {t("ins_add")}</button>
          ) : (
            <>
              <Field id="bbg-f-insuranceCompany" label={t("ins_company")} optional={t("opt_label")}>
                <input class="bbg-input" value={form.insuranceCompany} onInput={(e) => set("insuranceCompany", (e.target as HTMLInputElement).value)} />
              </Field>
              <Field id="bbg-f-insuranceMemberId" label={t("ins_member")} optional={t("opt_label")}>
                <input class="bbg-input" value={form.insuranceMemberId} onInput={(e) => set("insuranceMemberId", (e.target as HTMLInputElement).value)} />
              </Field>
            </>
          )}

          {submitErr ? (
            <div role="alert">
              <div class="bbg-err" style="text-align:center;margin:8px 0">{submitErr}</div>
              <button class="bbg-btn bbg-btn-outline" onClick={() => { setSubmitErr(null); setStep("time"); }}>
                {t("book_failed_cta")}
              </button>
            </div>
          ) : null}
          <div style="margin-top:8px">
            <PrimaryButton onClick={submit} loading={submitting} loadingLabel={t("review_booking")}>{t("review_submit")}</PrimaryButton>
          </div>
          <div class="bbg-actions" style="justify-content:center">
            <BackButton label={t("c_back")} onClick={() => setStep("time")} />
          </div>
          <p class="bbg-recaptcha">{t("review_recaptcha")}</p>
        </div>
      )}

      {step === "success" && location && (
        <div class="bbg-success">
          <CheckMark />
          <h2>{t("success_title")}</h2>
          <p class="bbg-sub">{t("success_msg")}</p>
          {slot ? <div class="when">{slot.datetime}</div> : null}
          <p class="bbg-sub">
            <a class="bbg-maplink" href={LOCATIONS[location].mapsUrl} target="_blank" rel="noopener">
              {LOCATIONS[location].addressLines.join(", ")}
            </a>
          </p>
          {slot ? (
            <div style="margin-top:14px">
              <p class="bbg-dayhead" style="text-align:center">{t("success_cal")}</p>
              <div class="bbg-calrow">
                <a
                  class="bbg-btn bbg-btn-primary"
                  href={gcalUrl(result?.appointmentTime ?? slot.iso, location)}
                  target="_blank" rel="noopener"
                  onClick={() => track("calendar_added", { kind: "google" })}
                >
                  Google Calendar
                </a>
                <button
                  class="bbg-btn bbg-btn-outline"
                  onClick={() => { downloadICS(buildICS(result?.appointmentTime ?? slot.iso, location)); track("calendar_added", { kind: "ics" }); }}
                >
                  Apple / Outlook
                </button>
              </div>
            </div>
          ) : null}
          <p class="bbg-note"><a href={officePhone(location).href}>{t("success_call")}: {officePhone(location).phone}</a></p>
        </div>
      )}

      {step === "waitlist_done" && (
        <div class="bbg-success">
          <CheckMark />
          <h2>{t("success_title")}</h2>
          <p class="bbg-sub">{t("waitlist_done")}</p>
          <p class="bbg-note"><a href={officePhone(location).href}>{t("success_call")}: {officePhone(location).phone}</a></p>
        </div>
      )}

      {step === "handoff" && embedUrl && (
        <div>
          <h2 class="bbg-title">{t("handoff_title")}</h2>
          <p class="bbg-sub">{t("handoff_msg")}</p>
          {/* Ortho2's booking SPA, framed inline. Turnstile + token mint + booking
              all run on Ortho2's own origin (valid sitekey there); allow-same-origin
              lets the SPA use its own storage but grants no access to our origin. */}
          <iframe
            class="bbg-embed"
            src={embedUrl}
            title={t("handoff_title")}
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
          />
          <p class="bbg-note">
            {t("handoff_fallback")}{" "}
            <a href={embedUrl} target="_blank" rel="noopener" onClick={() => track("ortho2_handoff_click", { locationId: location })}>
              {t("handoff_cta")}
            </a>
          </p>
          <p class="bbg-note"><a href={officePhone(location).href}>{t("success_call")}: {officePhone(location).phone}</a></p>
        </div>
      )}

      <p class="bbg-foot">{t("poweredBy")}</p>
    </div>
  );
}

function TimeStep(props: {
  t: (k: I18nKey) => string;
  /** Widget locale + clinic timezone, for the day-chip labels. */
  locale: string;
  tz: string;
  officeName: string;
  slots: Slot[] | null;
  loading: boolean;
  /** The slot fetch errored (as opposed to returning zero slots). */
  failed: boolean;
  /** We came back here because the chosen opening was taken at confirm time. */
  slotTaken: boolean;
  onRetry: () => void;
  submitting: boolean;
  onPick: (s: Slot) => void;
  /** Back to the contact-info (lead) step, values retained. */
  onBack: () => void;
  onChangeOffice: () => void;
  onWaitlist: () => void;
  /** Hide the "Change office" affordance for single-office brands. */
  multiOffice: boolean;
}) {
  const { t, slots, loading } = props;
  // Hooks must precede the conditional returns below.
  const [pickedDay, setPickedDay] = useState<string | null>(null);
  const dayStripRef = useRef<HTMLDivElement>(null);
  // Cap the chip wall (19 chips on one day dilutes the hero and buries the
  // actions); the designed-but-unwired time_more/time_fewer expander now works.
  const [showAllSlots, setShowAllSlots] = useState(false);
  // Past ~3s the wait needs to read as live work, not a hang.
  const [slowLoad, setSlowLoad] = useState(false);
  const isLoading = loading || slots === null;
  useEffect(() => {
    if (!isLoading) { setSlowLoad(false); return; }
    const timer = setTimeout(() => setSlowLoad(true), 3000);
    return () => clearTimeout(timer);
  }, [isLoading]);
  // The day strip scrolls on X only. A mouse wheel emits deltaY with deltaX 0,
  // and an element with no vertical overflow cannot consume vertical delta, so
  // the browser hands it to the nearest vertical scroller — which, in the embed,
  // is the practice page behind the iframe. Windows users therefore scrolled the
  // whole page instead of the dates (Mason Petersen, 2026-08-06). Mac trackpads
  // emit deltaX on a two-finger swipe, which the strip already consumes, which is
  // why this never showed up in our own testing. Translate vertical wheel into
  // horizontal scroll here.
  useEffect(() => {
    const el = dayStripRef.current;
    if (!el) return;
    // Scroll snapping does not rest at 0 / scrollWidth-clientWidth: it parks on
    // the container's scroll padding (left end sits at padding-left, 2px here)
    // and can overshoot max by a device-pixel fraction (736.5 vs 736). Both look
    // like "room to move" to a naive clamp, which then preventDefaults and lets
    // snap bounce it right back, trapping the wheel at the ends. Derive the
    // slack from the actual padding rather than hardcoding it; +1 covers the
    // fractional overshoot. Read once, not per event.
    const cs = getComputedStyle(el);
    const snapSlack = Math.max(parseFloat(cs.paddingLeft) || 0, parseFloat(cs.paddingRight) || 0) + 1;
    const onWheel = (e: WheelEvent) => {
      // Dominant-axis test: leave genuine horizontal input (trackpad swipe,
      // shift+wheel) to the browser's native handling.
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
      // deltaMode 1 = lines (Firefox), 2 = pages; 0 = pixels.
      const px = e.deltaMode === 1 ? e.deltaY * 16
        : e.deltaMode === 2 ? e.deltaY * el.clientWidth
          : e.deltaY;
      const max = el.scrollWidth - el.clientWidth;
      // Room left in the direction of travel. At either end, do NOT swallow the
      // event — the page should keep scrolling normally rather than trapping the
      // wheel over the strip.
      const room = px > 0 ? max - el.scrollLeft : el.scrollLeft;
      if (room <= snapSlack) return;
      e.preventDefault();
      el.scrollLeft = Math.min(max, Math.max(0, el.scrollLeft + px));
    };
    // Must be non-passive; a passive listener cannot preventDefault.
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [isLoading]);
  if (props.failed) {
    return (
      <div>
        <h2 class="bbg-title">{t("time_title")}</h2>
        <p class="bbg-sub">{t("slots_error")}</p>
        <PrimaryButton onClick={props.onRetry}>{t("c_retry")}</PrimaryButton>
        <div class="bbg-actions" style="justify-content:center">
          <BackButton label={t("c_back")} onClick={props.onBack} />
        </div>
      </div>
    );
  }
  if (isLoading) {
    // Skeleton of the real layout (hero, day strip, slot rows) so the wait
    // reads as content arriving rather than an indeterminate spinner.
    return (
      <div>
        <h2 class="bbg-title">{t("time_title")}</h2>
        <div class="bbg-skel bbg-skel-hero" />
        <div class="bbg-skel-strip">
          {[0, 1, 2, 3].map((i) => <div key={i} class="bbg-skel bbg-skel-chip" />)}
        </div>
        <div class="bbg-skel-row">
          {[0, 1, 2].map((i) => <div key={i} class="bbg-skel bbg-skel-slot" />)}
        </div>
        <div class="bbg-loading">
          <span class="bbg-spin" /> {slowLoad ? t("time_loading_slow").replace("{office}", props.officeName) : t("time_loading")}
        </div>
      </div>
    );
  }
  if (slots.length === 0) {
    // Lead is already captured + persisted, so this is a one-tap callback.
    return (
      <div>
        <h2 class="bbg-title">{t("time_title")}</h2>
        {props.slotTaken ? <p class="bbg-notice" role="status">{t("slot_taken")}</p> : null}
        <p class="bbg-sub">{t("time_none")}</p>
        <PrimaryButton onClick={props.onWaitlist} loading={props.submitting} loadingLabel={t("review_booking")}>{t("time_waitlist_cta")}</PrimaryButton>
        <div class="bbg-actions" style="justify-content:center">
          <BackButton label={t("c_back")} onClick={props.onBack} />
          {props.multiOffice ? <BackButton label={t("time_change_office")} onClick={props.onChangeOffice} /> : null}
        </div>
      </div>
    );
  }
  const soonest = slots[0];
  // Slots arrive soonest-first, so insertion order keeps the days chronological.
  const byDay = new Map<string, Slot[]>();
  for (const s of slots) {
    const arr = byDay.get(s.date);
    if (arr) arr.push(s);
    else byDay.set(s.date, [s]);
  }
  const dayKeys = [...byDay.keys()];
  const day = pickedDay && byDay.has(pickedDay) ? pickedDay : dayKeys[0];
  const list = byDay.get(day)!;
  const morning = list.filter((s) => s.hour < 12);
  const afternoon = list.filter((s) => s.hour >= 12);

  const tag = props.locale === "es" ? "es-US" : "en-US";
  const dowFmt = new Intl.DateTimeFormat(tag, { weekday: "short", timeZone: props.tz });
  const domFmt = new Intl.DateTimeFormat(tag, { month: "short", day: "numeric", timeZone: props.tz });

  // Absent days should read as policy, not failure: when availability spans
  // only a few distinct weekdays, say so honestly, derived from the real data.
  const wdayFmt = new Intl.DateTimeFormat(tag, { weekday: "long", timeZone: props.tz });
  const weekdays: string[] = [];
  for (const s of slots) {
    const w = wdayFmt.format(new Date(s.iso));
    if (!weekdays.includes(w)) weekdays.push(w);
  }
  const pluralDay = (w: string) => (props.locale === "es" ? (w.endsWith("s") ? w : `${w}s`) : `${w}s`);
  const joined = weekdays.map(pluralDay).join(props.locale === "es" ? " y " : " & ");
  const daysNote = weekdays.length > 0 && weekdays.length <= 3 ? t("time_days_note").replace("{days}", joined) : null;
  // The server pre-formats Slot.date in en-US; re-format locally so ES mode
  // shows a localized date in the hero card too.
  const longFmt = new Intl.DateTimeFormat(tag, { weekday: "long", month: "long", day: "numeric", timeZone: props.tz });

  const SLOT_CAP = 8;
  const capped = (g: Slot[]) => (showAllSlots ? g : g.slice(0, SLOT_CAP));
  const split = morning.length > 0 && afternoon.length > 0;
  const overflow = split ? morning.length > SLOT_CAP || afternoon.length > SLOT_CAP : list.length > SLOT_CAP;

  const chips = (group: Slot[]) => (
    <div class="bbg-slotrow">
      {capped(group).map((s) => (
        <button key={s.searchKey} class="bbg-slot" onClick={() => props.onPick(s)}>{s.time}</button>
      ))}
    </div>
  );

  return (
    <div>
      <h2 class="bbg-title">{t("time_title")}</h2>
      {props.slotTaken ? <p class="bbg-notice" role="status">{t("slot_taken")}</p> : null}
      <div class="bbg-soonest">
        <div class="label">{t("time_soonest")}</div>
        <div class="when">{longFmt.format(new Date(soonest.iso))} · {soonest.time}</div>
        <PrimaryButton onClick={() => props.onPick(soonest)}>{soonest.time} — {t("loc_select")}</PrimaryButton>
      </div>

      <p class="bbg-dayhead">{t("time_pick_day")}</p>
      <div class="bbg-daystrip" ref={dayStripRef}>
        {dayKeys.map((k) => {
          const group = byDay.get(k)!;
          const d = new Date(group[0].iso);
          return (
            <button
              key={k}
              class={`bbg-daychip${k === day ? " sel" : ""}`}
              onClick={() => { setPickedDay(k); setShowAllSlots(false); track("day_selected", { date: group[0].iso.slice(0, 10) }); }}
            >
              <span class="dow">{dowFmt.format(d)}</span>
              <span class="dom">{domFmt.format(d)}</span>
              <span class="cnt">{group.length} {t("time_open")}</span>
            </button>
          );
        })}
      </div>
      {daysNote ? <p class="bbg-daysnote">{daysNote}</p> : null}

      {split ? (
        <>
          <p class="bbg-dayhead">{t("time_morning")}</p>
          {chips(morning)}
          <p class="bbg-dayhead" style="margin-top:10px">{t("time_afternoon")}</p>
          {chips(afternoon)}
        </>
      ) : (
        chips(list)
      )}
      {overflow ? (
        <div style="text-align:center;margin-top:10px">
          <button class="bbg-btn bbg-btn-ghost" onClick={() => setShowAllSlots((s) => !s)}>
            {showAllSlots ? t("time_fewer") : t("time_more")}
          </button>
        </div>
      ) : null}

      {/* Escape hatch for visitors who see times but can't make any work — the
          lead is already captured, so the callback is one tap. */}
      <p class="bbg-waitlist-note">
        <button class="bbg-linkbtn" disabled={props.submitting} onClick={props.onWaitlist}>
          {t("time_waitlist_link")}
        </button>
      </p>

      <div class="bbg-actions" style="justify-content:center">
        <BackButton label={t("c_back")} onClick={props.onBack} />
        {props.multiOffice ? <BackButton label={t("time_change_office")} onClick={props.onChangeOffice} /> : null}
      </div>
    </div>
  );
}
